import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, UserCog, Eye, Pencil, KeyRound, Truck, Calculator, ClipboardCheck, UserCheck, Search } from "lucide-react";

const ROLE_LABEL = {
  super_admin: { text: "Super Admin", cls: "bg-red-100 text-red-800 border-red-200", icon: ShieldCheck },
  hr: { text: "HR", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: UserCog },
  // Rev. 2026-08-05 — Added hr_admin + hr_maker so Phase B maker-checker
  // roles render in the dropdown. Before this, the Edit User modal
  // showed a blank Role field for anyone whose DB role was hr_admin,
  // and Super Admins accidentally downgraded them to plain "hr" on save.
  hr_admin: { text: "HR Admin", cls: "bg-blue-100 text-blue-900 border-blue-300", icon: UserCog },
  hr_maker: { text: "HR Maker", cls: "bg-sky-100 text-sky-800 border-sky-200", icon: UserCog },
  unit_manager: { text: "Unit Manager", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: Eye },
  accounts: { text: "Accounts", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Calculator },
  logistics_manager: { text: "Logistics Manager", cls: "bg-amber-100 text-amber-900 border-amber-200", icon: Truck },
  logistics_executive: { text: "Logistics Executive", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Truck },
  mis: { text: "MIS", cls: "bg-purple-100 text-purple-800 border-purple-200", icon: ClipboardCheck },
  ea: { text: "EA", cls: "bg-red-100 text-red-800 border-red-200", icon: UserCheck },
};

// Order shown in dropdowns + role filters
const CONSOLE_ROLES = [
  "super_admin", "hr", "hr_admin", "hr_maker",
  "unit_manager", "accounts",
  "logistics_manager", "logistics_executive", "mis", "ea",
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "hr", unit_id: "", password: "" });
  const [busy, setBusy] = useState(false);

  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", email: "", name: "", role: "hr", unit_id: "" });
  const [editBusy, setEditBusy] = useState(false);

  // Password reset state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const load = async () => {
    try {
      const [u, ut] = await Promise.all([api.get("/users"), api.get("/units")]);
      setUsers(u.data);
      setUnits(ut.data);
    } catch {
      toast.error("Failed to load users");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        password: form.password,
        unit_id: form.role === "unit_manager" ? form.unit_id || null : null,
      };
      await api.post("/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", name: "", role: "hr", unit_id: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      email: u.email,
      name: u.name,
      role: u.role,
      unit_id: u.unit_id || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {
        email: editForm.email.trim().toLowerCase(),
        name: editForm.name.trim(),
        role: editForm.role,
        unit_id: editForm.role === "unit_manager" ? editForm.unit_id || null : null,
      };
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEditBusy(false);
    }
  };

  const openPwd = (u) => {
    setPwdTarget(u);
    setNewPwd("");
    setPwdOpen(true);
  };

  const submitPwd = async () => {
    if (!pwdTarget) return;
    setPwdBusy(true);
    try {
      await api.post(`/users/${pwdTarget.id}/reset-password`, { password: newPwd });
      toast.success(`Password reset for ${pwdTarget.email}. They must change it on next login.`);
      setPwdOpen(false);
      setPwdTarget(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPwdBusy(false);
    }
  };

  const unitName = (id) => units.find((u) => u.id === id)?.name || "—";

  const [filterRole, setFilterRole] = useState("all");
  const [search, setSearch] = useState("");

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const filteredUsers = users.filter((u) => {
    if (filterRole !== "all" && u.role !== filterRole) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.name?.toLowerCase().includes(q)
          && !u.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Settings</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Users & Roles</h1>
          <p className="text-sm text-slate-500 mt-1">Invite team members and assign roles.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" className="brand-bg brand-bg-hover text-white">
              <Plus className="h-4 w-4 mr-2" /> Invite User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Chivo" }}>Invite a new user</DialogTitle>
              <DialogDescription>
                Create a workspace account with a temporary password.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Full name</Label>
                <Input data-testid="iu-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="iu-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="iu-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONSOLE_ROLES.map(r => (
                      <SelectItem key={r} value={r}>{ROLE_LABEL[r].text}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.role === "unit_manager" && (
                <div>
                  <Label>Assigned Unit</Label>
                  <Select value={form.unit_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}>
                    <SelectTrigger data-testid="iu-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>
                      {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Temporary password</Label>
                <Input
                  data-testid="iu-password"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Min 8 chars, 1 upper, 1 number, 1 special"
                />
                <p className="text-xs text-slate-500 mt-1">User will be required to change it on first login.</p>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="iu-submit-btn" onClick={submit} disabled={busy} className="brand-bg brand-bg-hover text-white">
                {busy ? "Creating..." : "Create user"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="users-search"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-7 w-64 text-xs"
          />
        </div>
        <button
          data-testid="role-filter-all"
          onClick={() => setFilterRole("all")}
          className={`text-xs px-2.5 py-1 rounded-full border ${
            filterRole === "all"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}>
          All <span className="opacity-70 ml-1">({users.length})</span>
        </button>
        {CONSOLE_ROLES.map((r) => {
          const meta = ROLE_LABEL[r];
          const count = roleCounts[r] || 0;
          const active = filterRole === r;
          return (
            <button
              key={r}
              data-testid={`role-filter-${r}`}
              onClick={() => setFilterRole(r)}
              className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : `${meta.cls} border`
              }`}>
              <meta.icon className="h-3 w-3" />
              {meta.text}
              <span className={`${active ? "text-white/80" : "opacity-70"} ml-0.5`}>({count})</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Assigned Unit</th>
              <th className="px-4 py-3 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.map((u) => {
              const r = ROLE_LABEL[u.role] || ROLE_LABEL.hr;
              const Icon = r.icon;
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`${r.cls} border`}>
                      <Icon className="h-3 w-3 mr-1" />
                      {r.text}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {u.role === "unit_manager" ? unitName(u.unit_id) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`edit-user-${u.email}`}
                        onClick={() => openEdit(u)}
                        title="Edit user"
                        className="p-1.5 hover:bg-slate-100 rounded text-slate-600 hover:text-slate-900"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        data-testid={`reset-pwd-${u.email}`}
                        onClick={() => openPwd(u)}
                        title="Reset password"
                        className="p-1.5 hover:bg-amber-50 rounded text-amber-700 hover:text-amber-900"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>
                      {u.id !== me.id && (
                        <button
                          data-testid={`del-user-${u.email}`}
                          onClick={() => onDelete(u)}
                          title="Delete user"
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit User dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Edit user</DialogTitle>
            <DialogDescription>
              Update name, email, role, or assigned unit. Use the key icon to reset password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full name</Label>
              <Input
                data-testid="eu-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="eu-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="eu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONSOLE_ROLES.map(r => (
                    <SelectItem key={r} value={r}>{ROLE_LABEL[r].text}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "unit_manager" && (
              <div>
                <Label>Assigned Unit</Label>
                <Select
                  value={editForm.unit_id || undefined}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, unit_id: v }))}
                >
                  <SelectTrigger data-testid="eu-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="eu-submit-btn"
              onClick={submitEdit}
              disabled={editBusy}
              className="brand-bg brand-bg-hover text-white"
            >
              {editBusy ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Reset password</DialogTitle>
            <DialogDescription>
              {pwdTarget
                ? `Set a new temporary password for ${pwdTarget.name} (${pwdTarget.email}). They will be forced to change it on next login.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New temporary password</Label>
              <Input
                data-testid="pwd-new"
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min 8 chars, 1 upper, 1 number, 1 special"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="pwd-submit-btn"
              onClick={submitPwd}
              disabled={pwdBusy || !newPwd}
              className="brand-bg brand-bg-hover text-white"
            >
              {pwdBusy ? "Resetting..." : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
